import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CardMedia, Typography, Button } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { makeStyles } from '@material-ui/core/styles';
import currentNews from '../Dashboard/newsData';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.custom.pageBackground || '#f8fafc',
  },

  wrapper: {
    width: '100%',
    maxWidth: '960px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '28px 24px 80px',
    boxSizing: 'border-box',

    [theme.breakpoints.down('sm')]: {
      padding: '24px 18px 60px',
    },
  },

  back: {
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: '32px',
    color: '#365477',
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    padding: 0,
    minWidth: 0,

    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },

    '& .MuiButton-startIcon': {
      marginRight: '8px',
    },
  },

  header: {
    marginBottom: '42px',
  },

  metadata: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '20px',
  },

  category: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 13px',
    border: '1px solid #d4dce5',
    borderRadius: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.55)',
    color: '#31577c',
    fontSize: '12px',
    fontWeight: 500,
  },

  metadataText: {
    color: '#49637d',
    fontSize: '13px',
  },

  metadataSeparator: {
    color: '#7890a8',
    fontSize: '14px',
  },

  time: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },

  timeIcon: {
    fontSize: '14px',
    color: '#49637d',
  },

  title: {
    marginBottom: '22px',
    color: '#172238',
    fontSize: '50px',
    fontWeight: 700,
    lineHeight: 1.03,
    letterSpacing: '-1.8px',
    textAlign: 'center',

    [theme.breakpoints.down('sm')]: {
      fontSize: '38px',
      letterSpacing: '-1px',
    },
  },

  description: {
    marginBottom: '42px',
    color: '#48627e',
    fontSize: '18px',
    lineHeight: 1.65,

    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },

  heroImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '520px',
    objectFit: 'cover',
    borderRadius: '20px',
    display: 'block',
    marginBottom: '42px',
  },

  article: {
    color: '#49637d',
    fontSize: '17px',
    lineHeight: 1.65,

    '& p': {
      marginTop: 0,
      marginBottom: '22px',
    },
  },

  paragraph: {
    marginBottom: '22px',
  },

  heading: {
    marginTop: '38px',
    marginBottom: '18px',
    color: '#101c30',
    fontSize: '25px',
    fontWeight: 700,
    lineHeight: 1.25,

    [theme.breakpoints.down('sm')]: {
      fontSize: '22px',
    },
  },

  list: {
    marginTop: '8px',
    marginBottom: '30px',
    paddingLeft: '25px',
    color: '#49637d',

    '& li': {
      paddingLeft: '5px',
      marginBottom: '12px',
      fontSize: '17px',
      lineHeight: 1.55,
    },

    '& li::marker': {
      color: '#67c8ed',
      fontSize: '16px',
    },
  },

  authorSection: {
    marginTop: '55px',
    paddingTop: '30px',
    borderTop: '1px solid #dce3e9',
  },

  authorTitle: {
    color: '#49637d',
    fontSize: '14px',
    marginBottom: '8px',
  },

  authorName: {
    color: '#172238',
    fontWeight: 500,
  },

  authorDescription: {
    maxWidth: '760px',
    color: '#69809a',
    fontSize: '13px',
    lineHeight: 1.6,
  },
}));

export default function NewsView() {
  const { t } = useTranslation();
  const classes = useStyles();

  const news = currentNews;

  const category = news.category || 'Cultura de ejecución';
  const readTime = news.readTime || 5;
  const description = news.description || '';

  const renderContentBlock = (block, index) => {
    // Permite mantener compatibilidad con el formato actual:
    // content: ['Párrafo 1', 'Párrafo 2', ...]
    if (block.type === 'paragraph') {
      return (
        <Typography
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          component="p"
          className={classes.paragraph}
        >
          {block.text}
        </Typography>
      );
    }

    // Heading
    if (block.type === 'heading') {
      return (
        <Typography
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          component="h2"
          className={classes.heading}
        >
          {block.text}
        </Typography>
      );
    }

    // Lista
    if (block.type === 'list') {
      return (
        <ul
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={classes.list}
        >
          {block.items.map((item, itemIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={itemIndex}>{item}</li>
          ))}
        </ul>
      );
    }

    return null;
  };

  return (
    <div className={classes.page}>
      <main className={classes.wrapper}>
        <Button
          className={classes.back}
          component={Link}
          to="/home"
          startIcon={<ArrowBackIcon />}
        >
          {t('layout.tooltips.home') || 'Volver al blog'}
        </Button>

        <header className={classes.header}>
          <div className={classes.metadata}>
            <span className={classes.category}>{category}</span>

            <Typography className={classes.metadataText}>
              {news.date}
            </Typography>

            <span className={classes.metadataSeparator}>•</span>

            <Typography className={`${classes.metadataText} ${classes.time}`}>
              <AccessTimeIcon className={classes.timeIcon} />
              {readTime} min
            </Typography>
          </div>

          <Typography component="h1" className={classes.title}>
            {news.title}
          </Typography>

          {description && (
            <Typography component="p" className={classes.description}>
              {description}
            </Typography>
          )}

          <CardMedia
            component="img"
            className={classes.heroImage}
            image={news.image}
            alt={news.title}
          />
        </header>

        <article className={classes.article}>
          {news.content && news.content.map(renderContentBlock)}
        </article>

        {news.author && (
          <section className={classes.authorSection}>
            <Typography className={classes.authorTitle}>
              Publicado por{' '}
              <span className={classes.authorName}>{news.author.name}</span>
            </Typography>

            {news.author.description && (
              <Typography className={classes.authorDescription}>
                {news.author.description}
              </Typography>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
